import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { ArrowUpDown } from "lucide-react";

// Function to format dates in local timezone with consistent formatting
export const formatDateToLocalTimezone = (dateString) => {
    if (!dateString) return null;

    const date = new Date(dateString);

    // Format: "MM/DD/YYYY, hh:mm AM/PM"
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
    });
};

// Column definitions for the Pages DataTable
export const getColumns = (navigate, constructPageUrl, organization, template, orgId, templateId) => [
    {
        id: "select",
        header: ({ table }) => (
            <div className="w-[50px] flex justify-center">
                <Checkbox
                    checked={
                        table.getIsAllRowsSelected() ||
                        (table.getIsSomeRowsSelected() && "indeterminate")
                    }
                    onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
                    aria-label="Select all"
                />
            </div>
        ),
        cell: ({ row }) => (
            <div className="w-[50px] flex justify-center">
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                />
            </div>
        ),
        enableSorting: false,
        enableHiding: false,
        meta: {
            excludeFromCSV: true // Don't include checkbox column in CSV
        }
    },
    {
        accessorKey: "keyword_phrase",
        header: ({ column }) => (
            <div className="w-[200px]">
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    className="w-full justify-start"
                >
                    Keyword Phrase
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            </div>
        ),
        cell: ({ row }) => (
            <div className="w-[200px] font-medium overflow-hidden text-ellipsis whitespace-nowrap" title={row.original.keyword_phrase}>
                {row.original.keyword_phrase}
            </div>
        ),
    },
    {
        accessorKey: "url",
        header: () => <div className="w-[300px] pl-4">URL</div>,
        cell: ({ row }) => {
            const url = constructPageUrl(template, row.original);

            return (
                <div className="w-[300px] pl-4 overflow-hidden">
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline block w-full overflow-hidden text-ellipsis whitespace-nowrap"
                        title={url}
                    >
                        {url}
                    </a>
                </div>
            );
        },
        // For CSV export - format the URL properly
        accessorFn: (row) => constructPageUrl(template, row)
    },
    {
        accessorKey: "total_views",
        header: ({ column }) => (
            <div className="w-[100px] text-center">
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    className="w-full h-auto py-2"
                >
                    <div className="flex flex-col items-center">
                        <span>Total</span>
                        <div className="flex items-center">
                            <span>Views</span>
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                        </div>
                    </div>
                </Button>
            </div>
        ),
        cell: ({ row }) => <div className="w-[100px] text-center">{row.original.total_views || 0}</div>,
    },
    {
        accessorKey: "last_viewed_on",
        header: ({ column }) => (
            <div className="w-[150px] text-center">
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    className="w-full h-auto py-2"
                >
                    <div className="flex flex-col items-center">
                        <span>Last</span>
                        <div className="flex items-center">
                            <span>Viewed On</span>
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                        </div>
                    </div>
                </Button>
            </div>
        ),
        cell: ({ row }) => (
            <div className="w-[150px] text-center">
                {row.original.last_viewed_on
                    ? formatDateToLocalTimezone(row.original.last_viewed_on)
                    : "Never"}
            </div>
        ),
        // For CSV export - format the date properly
        accessorFn: (row) => row.last_viewed_on ? formatDateToLocalTimezone(row.last_viewed_on) : "Never"
    },
    {
        accessorKey: "created_on",
        header: ({ column }) => (
            <div className="w-[200px] text-center">
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    className="w-full justify-center"
                >
                    Created On
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            </div>
        ),
        cell: ({ row }) => (
            <div className="w-[200px] text-center">
                {row.original.created_on
                    ? formatDateToLocalTimezone(row.original.created_on)
                    : "Unknown"}
            </div>
        ),
        // For CSV export - format the date properly
        accessorFn: (row) => row.created_on ? formatDateToLocalTimezone(row.created_on) : "Unknown"
    }
]; 